import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";
import { useAuth } from "../contexts/AuthContext";
import { useSidebar } from "../contexts/SidebarContext";
import { trpcReact } from "../trpc_client";
import AuthModal from "../components/AuthModal";
import ImageUploader, { UploadedImage } from "../components/ImageUploader";

const USERNAME_MAX_LENGTH = 50;
const PROFILE_TEXT_MAX_LENGTH = 1000;
const MAX_ROOM_RENT = 50000;
const MAX_UTILITIES = 5000;
const MAX_DEPOSIT = 50000;
const MAX_ROOM_SIZE = 500;
const MAX_FLAT_SIZE = 2000;
const MAX_FLAT_MEMBERS = 10;
const MIN_AGE = 18;
const MAX_AGE = 99;

type Gender = "male" | "female" | "non_binary";

const FLAT_ATTRIBUTES = [
  { id: "0", label: "LGBTQ+ Friendly" },
  { id: "1", label: "Studio" },
  { id: "2", label: "Pet Friendly" },
  { id: "3", label: "Elevator" },
  { id: "4", label: "Parking" },
  { id: "5", label: "Garden" },
  { id: "6", label: "Balcony" },
  { id: "7", label: "Wheelchair Accessible" },
  { id: "8", label: "Dishwasher" },
  { id: "9", label: "Fireplace" },
  { id: "10", label: "Wifi" },
  { id: "11", label: "Washing Machine" },
  { id: "12", label: "Air Conditioning" },
  { id: "13", label: "Bathtub" },
  { id: "14", label: "TV" },
  { id: "15", label: "City Center" },
  { id: "16", label: "Recycling" },
  { id: "17", label: "Gym" },
  { id: "18", label: "Swimming Pool" },
  { id: "19", label: "Shops Nearby" },
  { id: "20", label: "Public Transport" },
];

const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "non_binary", label: "Non-binary" },
];

/* ─── Toggle Switch ──────────────────────────────────────────── */
const ToggleSwitch = ({
  value,
  onChange,
  accentColor,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  accentColor: string;
}) => (
  <button
    onClick={() => onChange(!value)}
    className="relative w-[52px] h-[30px] rounded-full transition-colors duration-200 shrink-0"
    style={{ backgroundColor: value ? accentColor : "rgba(2, 2, 16, 0.12)" }}
  >
    <div
      className="absolute top-[3px] w-6 h-6 rounded-full bg-white shadow-md transition-all duration-200"
      style={{ left: value ? 24 : 3 }}
    />
  </button>
);

/* ─── Number Stepper ─────────────────────────────────────────── */
const NumberStepper = ({
  value,
  onChange,
  min,
  max,
  colors,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  colors: ReturnType<typeof useColors>;
}) => (
  <div className="flex items-center gap-3">
    <button
      onClick={() => value > min && onChange(value - 1)}
      disabled={value <= min}
      className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-105 active:scale-95 disabled:opacity-30"
      style={{ backgroundColor: "rgba(2, 2, 16, 0.06)" }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke={colors.textNeutralDefault}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    </button>
    <span
      className="text-lg font-gnarly-semibold w-8 text-center tabular-nums"
      style={{ color: colors.textNeutralDefault }}
    >
      {value}
    </span>
    <button
      onClick={() => value < max && onChange(value + 1)}
      disabled={value >= max}
      className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-105 active:scale-95 disabled:opacity-30"
      style={{ backgroundColor: "rgba(2, 2, 16, 0.06)" }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke={colors.textNeutralDefault}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    </button>
  </div>
);

const EditFlat: React.FC = () => {
  const { flatId } = useParams<{ flatId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isCollapsed } = useSidebar();
  const colors = useColors();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [flatName, setFlatName] = useState("");
  const [memberCount, setMemberCount] = useState(2);
  const [description, setDescription] = useState("");
  const [lookingFor, setLookingFor] = useState<Gender[]>([]);
  const [lookingForAgeRange, setLookingForAgeRange] = useState<
    [number, number]
  >([18, 99]);
  const [isPermanent, setIsPermanent] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [rent, setRent] = useState("");
  const [utilities, setUtilities] = useState("");
  const [deposit, setDeposit] = useState("");
  const [roomSize, setRoomSize] = useState("");
  const [flatSize, setFlatSize] = useState("");
  const [addingFlatSize, setAddingFlatSize] = useState(false);
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [flatAttributes, setFlatAttributes] = useState<string[]>([]);
  const [question, setQuestion] = useState("");

  const [originalValues, setOriginalValues] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const flatIdNum = flatId ? parseInt(flatId, 10) : 0;

  const { data: flatData, isLoading: isLoadingFlat } =
    trpcReact.getflat.useQuery(
      { flat_id: flatIdNum },
      { enabled: !!flatId && !isNaN(flatIdNum) && flatIdNum > 0 },
    );

  const { data: userFlats } = trpcReact.getUserFlats.useQuery(undefined, {
    enabled: !!user,
  });

  const isOwner = useMemo(() => {
    if (!userFlats || !flatIdNum) return false;
    return userFlats.some((f) => f.flat.flat_id === flatIdNum);
  }, [userFlats, flatIdNum]);

  const updateFlatMutation = trpcReact.updateFlat.useMutation();
  const leaveFlatMutation = trpcReact.leaveFlat.useMutation();
  const deleteFlatMutation = trpcReact.deleteFlat.useMutation();

  useEffect(() => {
    if (flatData && !isInitialized) {
      const flat = flatData.flat;
      const room = flat.room?.[0];

      setFlatName(flat.name || "");
      setMemberCount(flat.member_count || 2);
      setDescription(flat.flat_text?.description || "");
      setLookingFor((room?.looking_for as Gender[]) || []);

      const ageRange = room?.looking_for_age_range;
      if (Array.isArray(ageRange) && ageRange.length === 2) {
        setLookingForAgeRange([ageRange[0], ageRange[1]]);
      }

      setIsPermanent(room?.permanent ?? true);
      setIsPublic(flat.public ?? true);

      if (room?.from_date) {
        setFromDate(new Date(room.from_date).toISOString().split("T")[0]);
      }
      if (room?.to_date) {
        setToDate(new Date(room.to_date).toISOString().split("T")[0]);
      }

      setRent(String(room?.rent || ""));
      setUtilities(String(room?.utilities || ""));
      setDeposit(String(room?.deposit || ""));

      const flatSizeNum = flat.size ? Number(flat.size) : -1;
      setAddingFlatSize(flatSizeNum > 0);
      if (flatSizeNum > 0) {
        setFlatSize(String(flatSizeNum));
      }
      setRoomSize(String(room?.size || ""));

      const imgs: UploadedImage[] = (flatData.urls || []).map(
        (url: string, index: number) => ({
          uri: url,
          object_name:
            flat.flat_objects?.objects?.[index] || `existing_${index}`,
          get_url: url,
          progress: 1,
        }),
      );
      setImages(imgs);

      const attrs = (flat.flat_attributes || []).map((attr: any) =>
        String(typeof attr === "object" ? attr.attribute : attr),
      );
      setFlatAttributes(attrs);

      setQuestion(flat.flat_question || "");

      setOriginalValues({
        flatName: flat.name || "",
        description: flat.flat_text?.description || "",
        rent: String(room?.rent || ""),
        utilities: String(room?.utilities || ""),
        deposit: String(room?.deposit || ""),
        roomSize: String(room?.size || ""),
        imagesCount: (flatData.urls || []).length,
        isPublic: flat.public ?? true,
        isPermanent: room?.permanent ?? true,
        memberCount: flat.member_count || 2,
      });

      setIsInitialized(true);
    }
  }, [flatData, isInitialized]);

  useEffect(() => {
    if (!originalValues) return;
    const changed =
      flatName !== originalValues.flatName ||
      description !== originalValues.description ||
      rent !== originalValues.rent ||
      utilities !== originalValues.utilities ||
      deposit !== originalValues.deposit ||
      roomSize !== originalValues.roomSize ||
      images.length !== originalValues.imagesCount ||
      isPublic !== originalValues.isPublic ||
      isPermanent !== originalValues.isPermanent ||
      memberCount !== originalValues.memberCount;
    setHasChanges(changed);
  }, [
    flatName,
    description,
    rent,
    utilities,
    deposit,
    roomSize,
    images,
    originalValues,
    isPublic,
    isPermanent,
    memberCount,
  ]);

  useEffect(() => {
    if (userFlats && !isOwner && !isLoadingFlat) {
      navigate(`/app/flat-view/${flatId}`, { replace: true });
    }
  }, [userFlats, isOwner, isLoadingFlat, navigate, flatId]);

  const handleCancel = () => {
    if (hasChanges) {
      if (
        window.confirm(
          t("confirm_leave") ||
            "You have unsaved changes. Are you sure you want to leave?",
        )
      ) {
        navigate(`/app/flat-view/${flatId}`);
      }
    } else {
      navigate(`/app/flat-view/${flatId}`);
    }
  };

  const handleSave = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (images.some((img) => img.progress < 1)) {
      setSubmitError(
        t("wait_for_uploads") || "Please wait for all images to upload",
      );
      return;
    }

    if (images.length < 1) {
      setSubmitError("You need at least 1 image");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const fromDateIso = fromDate
        ? new Date(fromDate).toISOString()
        : new Date().toISOString();
      const toDateIso = isPermanent
        ? fromDateIso
        : toDate
          ? new Date(toDate).toISOString()
          : fromDateIso;

      await updateFlatMutation.mutateAsync({
        flat_id: flatIdNum,
        flat_name: flatName.trim(),
        flat_member_count: Math.max(2, memberCount),
        flat_text: description.trim(),
        looking_for: lookingFor,
        looking_for_age_range: lookingForAgeRange,
        flat_size: addingFlatSize ? parseInt(flatSize) || -1 : -1,
        flat_object_names: images
          .filter((img) => img.object_name)
          .map((img) => img.object_name.replace(/^flat\//, "")),
        rent: parseInt(rent) || 0,
        utilities: parseInt(utilities) || 0,
        deposit: parseInt(deposit) || 0,
        room_size: parseInt(roomSize) || 0,
        permanent: isPermanent,
        from_date: fromDateIso,
        to_date: toDateIso,
        languages: [],
        flat_question: question,
        attributes: flatAttributes.map((attr) => parseInt(attr)),
        public: isPublic,
      });

      navigate(`/app/flat-view/${flatId}`, { replace: true });
    } catch (error: any) {
      console.error("Error updating flat:", error);
      setSubmitError(
        error.message ||
          t("update_flat_error") ||
          "Failed to update flat. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNumberInput = (
    value: string,
    setter: (val: string) => void,
    max: number,
  ) => {
    const cleaned = value.replace(/[^0-9]/g, "");
    if (cleaned === "" || parseInt(cleaned) <= max) {
      setter(cleaned);
    }
  };

  const toggleAttribute = (attrId: string) => {
    setFlatAttributes((prev) =>
      prev.includes(attrId)
        ? prev.filter((id) => id !== attrId)
        : [...prev, attrId],
    );
  };

  const toggleGender = (gender: Gender) => {
    setLookingFor((prev) =>
      prev.includes(gender)
        ? prev.filter((g) => g !== gender)
        : [...prev, gender],
    );
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLeaveFlat = async () => {
    try {
      await leaveFlatMutation.mutateAsync({ flat_id: flatIdNum });
      navigate("/flats", { replace: true });
    } catch (error: any) {
      console.error("Error leaving flat:", error);
      setSubmitError(
        error.message || t("leave_flat_error") || "Failed to leave flat.",
      );
    }
    setShowLeaveConfirm(false);
  };

  const handleDeleteFlat = async () => {
    try {
      await deleteFlatMutation.mutateAsync({ flat_id: flatIdNum });
      navigate("/flats", { replace: true });
    } catch (error: any) {
      console.error("Error deleting flat:", error);
      setSubmitError(
        error.message || t("delete_flat_error") || "Failed to delete flat.",
      );
    }
    setShowDeleteConfirm(false);
  };

  /* ─── Shared styling helpers ────────────────────────────────── */
  const sectionCard: React.CSSProperties = {
    backgroundColor: colors.containerNeutralSolidSurface,
    borderRadius: 20,
    boxShadow:
      "0 2px 12px rgba(2, 2, 16, 0.04), 0 0 0 1px rgba(2, 2, 16, 0.03)",
  };

  if (isLoadingFlat || !isInitialized) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.containerNeutralSolidSurface }}
      >
        <div className="text-center">
          <div
            className="w-12 h-12 border-4 rounded-full animate-spin mx-auto mb-4"
            style={{
              borderColor: colors.containerNeutralLightMedium,
              borderTopColor: colors.accentColor,
            }}
          />
          <p
            className="font-gnarly-regular"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("loading") || "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen font-gnarly-regular"
      style={{ backgroundColor: colors.containerNeutralSolidSurface }}
    >
      {/* Fixed Header */}
      <header
        className={`fixed top-0 right-0 z-40 transition-all duration-300 ease-in-out left-0 ${
          isCollapsed ? "lg:left-16" : "lg:left-56"
        }`}
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          boxShadow: "none",
        }}
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <button
            onClick={handleCancel}
            className="w-10 h-10 -ml-2 rounded-full flex items-center justify-center hover:scale-105 active:scale-95 transition-all duration-150"
            style={{ color: colors.textNeutralDefault }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
          </button>

          <h1
            className="text-lg font-gnarly-semibold tracking-tight"
            style={{ color: colors.textNeutralDefault }}
          >
            {t("edit_flat") || "Edit Flat"}
          </h1>

          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className="px-5 py-2.5 rounded-full font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.03] active:scale-[0.97] disabled:opacity-40"
            style={{
              backgroundColor: colors.accentColor,
              color: colors.textNeutralOnPrimary,
            }}
          >
            {isSubmitting ? t("saving") || "Saving..." : t("save") || "Save"}
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-24 pb-12 space-y-5">
        {submitError && (
          <div
            className="px-5 py-4 rounded-2xl text-sm font-gnarly-regular"
            style={{
              backgroundColor: "rgba(220, 54, 46, 0.07)",
              color: "#B02B25",
            }}
          >
            {submitError}
          </div>
        )}

        {/* ─── Flat Name ──────────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("flats_tab.add_flat.flat_name_title") || "Flat Name"}
          </label>
          <input
            type="text"
            value={flatName}
            onChange={(e) =>
              e.target.value.length <= USERNAME_MAX_LENGTH &&
              setFlatName(e.target.value)
            }
            maxLength={USERNAME_MAX_LENGTH}
            className="cf-input-boxed text-[15px]"
            style={{ color: colors.textNeutralDefault }}
            placeholder="e.g., Sunny Downtown WG"
          />
          <p
            className="mt-2 text-[11px]"
            style={{ color: colors.textNeutralMuted }}
          >
            {flatName.length}/{USERNAME_MAX_LENGTH}
          </p>
        </section>

        {/* ─── Description ────────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("flats_tab.add_flat.flat_text_title") || "Description"}
          </label>
          <textarea
            value={description}
            onChange={(e) =>
              e.target.value.length <= PROFILE_TEXT_MAX_LENGTH &&
              setDescription(e.target.value)
            }
            maxLength={PROFILE_TEXT_MAX_LENGTH}
            rows={5}
            className="cf-textarea text-[15px]"
            style={{ color: colors.textNeutralDefault }}
            placeholder="Tell us about your flat..."
          />
          <p
            className="mt-2 text-[11px]"
            style={{ color: colors.textNeutralMuted }}
          >
            {description.length}/{PROFILE_TEXT_MAX_LENGTH}
          </p>
        </section>

        {/* ─── Public / Permanent toggles ─────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <div className="flex items-center justify-between py-1">
            <div>
              <p
                className="text-[15px] font-gnarly-semibold"
                style={{ color: colors.textNeutralDefault }}
              >
                {t("flats_tab.edit_flat.public") || "Public"}
              </p>
              <p
                className="text-xs mt-0.5"
                style={{ color: colors.textNeutralMuted }}
              >
                {t("edit_flat_public_desc") ||
                  "Visible to people searching for flats"}
              </p>
            </div>
            <ToggleSwitch
              value={isPublic}
              onChange={setIsPublic}
              accentColor={colors.accentColor}
            />
          </div>

          <div
            className="my-4"
            style={{ height: 1, backgroundColor: "rgba(2, 2, 16, 0.06)" }}
          />

          <div className="flex items-center justify-between py-1">
            <div>
              <p
                className="text-[15px] font-gnarly-semibold"
                style={{ color: colors.textNeutralDefault }}
              >
                {t("flats_tab.edit_flat.is_permanent") || "Permanent"}
              </p>
              <p
                className="text-xs mt-0.5"
                style={{ color: colors.textNeutralMuted }}
              >
                {t("edit_flat_permanent_desc") ||
                  "No fixed end date for the lease"}
              </p>
            </div>
            <ToggleSwitch
              value={isPermanent}
              onChange={setIsPermanent}
              accentColor={colors.accentColor}
            />
          </div>

          {/* Date pickers */}
          <div
            className={`mt-4 grid gap-3 ${isPermanent ? "grid-cols-1" : "grid-cols-2"}`}
          >
            <div>
              <label
                className="block text-[11px] mb-1.5 font-gnarly-semibold"
                style={{ color: colors.textNeutralMuted }}
              >
                {t("from_date") || "From"}
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  if (toDate && e.target.value > toDate) {
                    setToDate(e.target.value);
                  }
                }}
                className="cf-input-boxed text-sm"
                style={{ color: colors.textNeutralDefault }}
              />
            </div>
            {!isPermanent && (
              <div>
                <label
                  className="block text-[11px] mb-1.5 font-gnarly-semibold"
                  style={{ color: colors.textNeutralMuted }}
                >
                  {t("to_date") || "To"}
                </label>
                <input
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(e) => setToDate(e.target.value)}
                  className="cf-input-boxed text-sm"
                  style={{ color: colors.textNeutralDefault }}
                />
              </div>
            )}
          </div>
        </section>

        {/* ─── Photos ─────────────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("flats_tab.add_flat.flat_images_title") || "Photos"}
          </label>
          <ImageUploader
            images={images}
            onImagesChange={setImages}
            onRemoveImage={handleRemoveImage}
            kind="flat"
            minImages={1}
            maxImages={9}
            showNumberBadge
          />
        </section>

        {/* ─── Members & Size ─────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-4"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("flats_tab.edit_flat.flat_member_count") || "Flat Details"}
          </label>

          {/* Member count */}
          <div className="flex items-center justify-between mb-5">
            <p
              className="text-[15px]"
              style={{ color: colors.textNeutralDefault }}
            >
              {t("flatmates") || "Flatmates"}
            </p>
            <NumberStepper
              value={memberCount}
              onChange={setMemberCount}
              min={2}
              max={MAX_FLAT_MEMBERS}
              colors={colors}
            />
          </div>

          {/* Room size */}
          <div className="flex items-center justify-between mb-5">
            <p
              className="text-[15px]"
              style={{ color: colors.textNeutralDefault }}
            >
              {t("flats_tab.add_flat.room_size") || "Room size"}
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                value={roomSize}
                onChange={(e) =>
                  handleNumberInput(e.target.value, setRoomSize, MAX_ROOM_SIZE)
                }
                placeholder="0"
                className="cf-input-boxed w-20 text-right text-sm !py-2.5 !px-3"
                style={{ color: colors.textNeutralDefault }}
              />
              <span
                className="text-sm"
                style={{ color: colors.textNeutralMuted }}
              >
                m²
              </span>
            </div>
          </div>

          {/* Flat size toggle */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAddingFlatSize(!addingFlatSize)}
                className="w-6 h-6 rounded-full flex items-center justify-center transition-all duration-150"
                style={{
                  backgroundColor: addingFlatSize
                    ? colors.accentColor
                    : "rgba(2, 2, 16, 0.08)",
                  color: addingFlatSize ? "white" : colors.textNeutralMuted,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {addingFlatSize ? (
                    <line x1="5" y1="12" x2="19" y2="12" />
                  ) : (
                    <>
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </>
                  )}
                </svg>
              </button>
              <span
                className="text-[15px] leading-none"
                style={{ color: colors.textNeutralDefault }}
              >
                {t("flat_size") || "Flat size"}
              </span>
            </div>
            {addingFlatSize && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={flatSize}
                  onChange={(e) =>
                    handleNumberInput(
                      e.target.value,
                      setFlatSize,
                      MAX_FLAT_SIZE,
                    )
                  }
                  placeholder="0"
                  className="cf-input-boxed w-20 text-right text-sm !py-2.5 !px-3"
                  style={{ color: colors.textNeutralDefault }}
                />
                <span
                  className="text-sm"
                  style={{ color: colors.textNeutralMuted }}
                >
                  m²
                </span>
              </div>
            )}
          </div>
        </section>

        {/* ─── Pricing ────────────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-4"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("flats_tab.add_flat.flat_price_title") || "Pricing"}
          </label>
          <div className="space-y-4">
            {[
              {
                label: t("flats_tab.add_flat.flat_rent") || "Monthly rent",
                value: rent,
                setter: setRent,
                max: MAX_ROOM_RENT,
              },
              {
                label: t("flats_tab.add_flat.flat_utilities") || "Utilities",
                value: utilities,
                setter: setUtilities,
                max: MAX_UTILITIES,
              },
              {
                label: t("flats_tab.add_flat.flat_deposit") || "Deposit",
                value: deposit,
                setter: setDeposit,
                max: MAX_DEPOSIT,
              },
            ].map((field) => (
              <div
                key={field.label}
                className="flex items-center justify-between"
              >
                <span
                  className="text-[15px]"
                  style={{ color: colors.textNeutralDefault }}
                >
                  {field.label}
                </span>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={field.value}
                    onChange={(e) =>
                      handleNumberInput(e.target.value, field.setter, field.max)
                    }
                    placeholder="0"
                    className="cf-input-boxed w-24 text-right text-sm !py-2.5 !px-3"
                    style={{ color: colors.textNeutralDefault }}
                  />
                  <span
                    className="text-sm w-8"
                    style={{ color: colors.textNeutralMuted }}
                  >
                    CHF
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Gender Preference ──────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("flats_tab.edit_flat.gender_preference") || "Gender Preference"}
          </label>
          <div className="flex gap-2">
            {GENDER_OPTIONS.map((opt) => {
              const isSelected = lookingFor.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => toggleGender(opt.value)}
                  className="flex-1 py-3 rounded-xl text-sm font-gnarly-semibold transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: isSelected
                      ? `${colors.accentColor}14`
                      : "rgba(2, 2, 16, 0.04)",
                    color: isSelected
                      ? colors.accentColor
                      : colors.textNeutralMuted,
                    border: `1.5px solid ${isSelected ? colors.accentColor : "transparent"}`,
                  }}
                >
                  {t(opt.value) || opt.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* ─── Age Range ──────────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("flats_tab.edit_flat.age_preference") || "Age Preference"}
          </label>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label
                className="block text-[11px] mb-1.5"
                style={{ color: colors.textNeutralMuted }}
              >
                {t("min") || "Min"}
              </label>
              <input
                type="number"
                min={MIN_AGE}
                max={lookingForAgeRange[1]}
                value={lookingForAgeRange[0]}
                onChange={(e) => {
                  const v = Math.max(
                    MIN_AGE,
                    Math.min(
                      parseInt(e.target.value) || MIN_AGE,
                      lookingForAgeRange[1],
                    ),
                  );
                  setLookingForAgeRange([v, lookingForAgeRange[1]]);
                }}
                className="cf-input-boxed text-center text-lg font-gnarly-semibold"
                style={{ color: colors.textNeutralDefault }}
              />
            </div>
            <span
              className="text-sm mt-5"
              style={{ color: colors.textNeutralMuted }}
            >
              —
            </span>
            <div className="flex-1">
              <label
                className="block text-[11px] mb-1.5"
                style={{ color: colors.textNeutralMuted }}
              >
                {t("max") || "Max"}
              </label>
              <input
                type="number"
                min={lookingForAgeRange[0]}
                max={MAX_AGE}
                value={lookingForAgeRange[1]}
                onChange={(e) => {
                  const v = Math.min(
                    MAX_AGE,
                    Math.max(
                      parseInt(e.target.value) || MAX_AGE,
                      lookingForAgeRange[0],
                    ),
                  );
                  setLookingForAgeRange([lookingForAgeRange[0], v]);
                }}
                className="cf-input-boxed text-center text-lg font-gnarly-semibold"
                style={{ color: colors.textNeutralDefault }}
              />
            </div>
          </div>
        </section>

        {/* ─── Attributes ─────────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("flats_tab.add_flat.flat_attributes_title") || "Attributes"}
          </label>
          <div className="flex flex-wrap gap-2">
            {FLAT_ATTRIBUTES.map((attr) => {
              const isSelected = flatAttributes.includes(attr.id);
              return (
                <button
                  key={attr.id}
                  onClick={() => toggleAttribute(attr.id)}
                  className="px-4 py-2.5 rounded-full text-sm font-gnarly-semibold transition-all duration-150 hover:scale-[1.02] active:scale-[0.97]"
                  style={{
                    backgroundColor: isSelected
                      ? `${colors.accentColor}14`
                      : "rgba(2, 2, 16, 0.04)",
                    color: isSelected
                      ? colors.accentColor
                      : colors.textNeutralDefault,
                    border: `1.5px solid ${isSelected ? colors.accentColor : "transparent"}`,
                  }}
                >
                  {attr.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* ─── Screening Question ─────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-3"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("flats_tab.add_flat.flat_question_title") ||
              "Screening Question"}{" "}
            <span className="normal-case tracking-normal font-gnarly-regular">
              ({t("optional") || "optional"})
            </span>
          </label>
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="cf-input-boxed text-[15px]"
            style={{ color: colors.textNeutralDefault }}
            placeholder={
              t("flats_tab.add_flat.flat_question_placeholder") ||
              "Ask something you'd like to know about applicants..."
            }
          />
        </section>

        {/* ─── Danger Zone ────────────────────────────────────── */}
        <section className="p-5" style={sectionCard}>
          <label
            className="block text-xs font-gnarly-semibold uppercase tracking-wider mb-4"
            style={{ color: "#B02B25" }}
          >
            {t("danger_zone") || "Danger Zone"}
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setShowLeaveConfirm(true)}
              disabled={leaveFlatMutation.isPending}
              className="flex-1 px-4 py-3.5 rounded-2xl font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
              style={{
                backgroundColor: "rgba(245, 158, 11, 0.1)",
                color: "#b45309",
              }}
            >
              {leaveFlatMutation.isPending
                ? t("leaving") || "Leaving..."
                : t("flats_tab.edit_flat.leave_flat_alert.title") ||
                  "Leave Flat"}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleteFlatMutation.isPending}
              className="flex-1 px-4 py-3.5 rounded-2xl font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
              style={{
                backgroundColor: "rgba(220, 54, 46, 0.07)",
                color: "#B02B25",
              }}
            >
              {deleteFlatMutation.isPending
                ? t("deleting") || "Deleting..."
                : t("flats_tab.edit_flat.delete_flat_alert.title") ||
                  "Delete Flat"}
            </button>
          </div>
        </section>
      </main>

      {/* ─── Leave Flat Confirmation Modal ────────────────────── */}
      {showLeaveConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            backgroundColor: "rgba(2, 2, 16, 0.45)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <div
            className="max-w-md w-full p-6"
            style={{
              ...sectionCard,
              animation: "scaleIn 0.2s ease-out",
              boxShadow:
                "0 24px 80px rgba(2, 2, 16, 0.2), 0 0 0 1px rgba(2, 2, 16, 0.05)",
            }}
          >
            <h2
              className="text-xl font-gnarly-semibold mb-2"
              style={{ color: colors.textNeutralDefault }}
            >
              {t("flats_tab.edit_flat.leave_flat_alert.title") || "Leave Flat"}
            </h2>
            <p
              className="mb-6 text-sm leading-relaxed"
              style={{ color: colors.textNeutralMuted }}
            >
              {t("flats_tab.edit_flat.leave_flat_alert.body") ||
                "Are you sure you want to leave this flat? You will lose access to all conversations and data associated with this flat."}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="flex-1 px-4 py-3 rounded-2xl font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  backgroundColor: "rgba(2, 2, 16, 0.05)",
                  color: colors.textNeutralDefault,
                }}
              >
                {t("cancel") || "Cancel"}
              </button>
              <button
                onClick={handleLeaveFlat}
                disabled={leaveFlatMutation.isPending}
                className="flex-1 px-4 py-3 rounded-2xl font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                style={{
                  backgroundColor: "rgba(245, 158, 11, 0.15)",
                  color: "#b45309",
                }}
              >
                {leaveFlatMutation.isPending
                  ? t("leaving") || "Leaving..."
                  : t("leave") || "Leave"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete Flat Confirmation Modal ───────────────────── */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            backgroundColor: "rgba(2, 2, 16, 0.45)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <div
            className="max-w-md w-full p-6"
            style={{
              ...sectionCard,
              animation: "scaleIn 0.2s ease-out",
              boxShadow:
                "0 24px 80px rgba(2, 2, 16, 0.2), 0 0 0 1px rgba(2, 2, 16, 0.05)",
            }}
          >
            <h2
              className="text-xl font-gnarly-semibold mb-2"
              style={{ color: colors.textNeutralDefault }}
            >
              {t("flats_tab.edit_flat.delete_flat_alert.title") ||
                "Delete Flat"}
            </h2>
            <p
              className="mb-6 text-sm leading-relaxed"
              style={{ color: colors.textNeutralMuted }}
            >
              {t("flats_tab.edit_flat.delete_flat_alert.body") ||
                "Are you sure you want to delete this flat? This action cannot be undone and all data will be permanently lost."}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-3 rounded-2xl font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  backgroundColor: "rgba(2, 2, 16, 0.05)",
                  color: colors.textNeutralDefault,
                }}
              >
                {t("cancel") || "Cancel"}
              </button>
              <button
                onClick={handleDeleteFlat}
                disabled={deleteFlatMutation.isPending}
                className="flex-1 px-4 py-3 rounded-2xl font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                style={{
                  backgroundColor: "rgba(220, 54, 46, 0.1)",
                  color: "#B02B25",
                }}
              >
                {deleteFlatMutation.isPending
                  ? t("deleting") || "Deleting..."
                  : t("delete") || "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          handleSave();
        }}
      />
    </div>
  );
};

export default EditFlat;
